import { Avatar, Button, Dialog, DropdownMenu } from '@protoss/ui/components';
import { CheckIcon } from '@radix-ui/react-icons';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { parseThemePreference, useThemePreference, type ThemePreference } from '../lib/theme';

import './confirm-delete-dialog.css';

/**
 * UserMenu - Header user dropdown menu
 *
 * Displays user avatar and name as trigger, with dropdown menu containing:
 * - Email verification warning (if not verified)
 * - Settings (placeholder for future implementation)
 * - Theme: Light / Dark / System (per browser, see lib/theme.ts)
 * - Log out (destructive; opens a confirmation dialog)
 *
 * Uses Radix Dropdown Menu for accessibility. The log-out confirmation Dialog is
 * rendered as a sibling of the menu (not nested) and driven by state, which is
 * the supported pattern for opening a dialog from a menu item without the two
 * components fighting over focus.
 * Depends on AuthContext (app-specific), so lives in apps/web.
 */
const THEME_OPTIONS: Array<{ value: ThemePreference; label: string }> = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'system', label: 'System' },
];

export function UserMenu() {
  const { user, appUser, signOut } = useAuth();
  const [themePreference, setThemePreference] = useThemePreference();
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [isSigningOut, setIsSigningOut] = React.useState(false);

  const handleSignOut = async () => {
    setIsSigningOut(true);
    try {
      await signOut();
      // On success the app redirects to sign-in and this tree unmounts, so we
      // intentionally do not reset isSigningOut here.
    } catch (error) {
      console.error('Sign out failed:', error);
      setIsSigningOut(false);
    }
  };

  const displayName = user?.displayName ?? user?.email ?? 'User';
  const isEmailVerified = user?.emailVerified ?? false;

  // Once the app user is loaded, treat avatarUrl as the authoritative source.
  // A null avatarUrl means "no real profile photo" - the Avatar component will
  // render initials. Do NOT fall through to user.photoURL in that case: Firebase
  // can return an auto-generated Google avatar URL even when no real photo exists,
  // which would bypass the intended initials fallback.
  //
  // user.photoURL is used only during the brief window before the backend
  // exchange has completed (appUser is still null) to avoid a blank avatar flash.
  const avatarSrc = appUser?.avatarUrl ?? user?.photoURL ?? null;

  return (
    <>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <button className="user-menu-trigger" aria-label="User menu">
            <Avatar
              src={avatarSrc}
              name={user?.displayName ?? user?.email ?? undefined}
              size="sm"
            />
            <span className="user-menu-trigger-name">{displayName}</span>
          </button>
        </DropdownMenu.Trigger>

        <DropdownMenu.Portal>
          <DropdownMenu.Content
            className="user-menu-content dropdown-content"
            align="end"
            sideOffset={8}
          >
            {!isEmailVerified && (
              <>
                <div className="user-menu-warning">
                  <span className="user-menu-warning-text">Email not verified</span>
                </div>
                <DropdownMenu.Separator className="dropdown-separator" />
              </>
            )}

            <DropdownMenu.Item
              className="user-menu-item dropdown-item"
              onSelect={() => void navigate('/settings')}
            >
              Settings
            </DropdownMenu.Item>

            <DropdownMenu.Separator className="dropdown-separator" />

            <DropdownMenu.Label className="user-menu-label">Theme</DropdownMenu.Label>
            <DropdownMenu.RadioGroup
              value={themePreference}
              onValueChange={(value) => setThemePreference(parseThemePreference(value))}
            >
              {THEME_OPTIONS.map((option) => (
                <DropdownMenu.RadioItem
                  key={option.value}
                  value={option.value}
                  className="user-menu-item dropdown-item"
                  // Keep the menu open so the change is visible while choosing.
                  onSelect={(event) => event.preventDefault()}
                >
                  <span className="user-menu-item__label">{option.label}</span>
                  <DropdownMenu.ItemIndicator className="user-menu-item__indicator">
                    <CheckIcon aria-hidden="true" />
                  </DropdownMenu.ItemIndicator>
                </DropdownMenu.RadioItem>
              ))}
            </DropdownMenu.RadioGroup>

            <DropdownMenu.Separator className="dropdown-separator" />

            <DropdownMenu.Item
              className="user-menu-item dropdown-item dropdown-item--danger"
              onSelect={() => setConfirmOpen(true)}
            >
              Log out
            </DropdownMenu.Item>

            <DropdownMenu.Arrow className="user-menu-arrow" />
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>

      <Dialog.Root
        open={confirmOpen}
        onOpenChange={(next) => {
          if (isSigningOut) return;
          setConfirmOpen(next);
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="confirm-delete-dialog__overlay" />
          <Dialog.Content className="confirm-delete-dialog__content">
            <div className="confirm-delete-dialog__header">
              <Dialog.Title className="confirm-delete-dialog__title">Log out?</Dialog.Title>
              <Dialog.Close asChild>
                <button
                  type="button"
                  className="confirm-delete-dialog__close"
                  aria-label="Close"
                  disabled={isSigningOut}
                >
                  ×
                </button>
              </Dialog.Close>
            </div>
            <div className="confirm-delete-dialog__body">
              <Dialog.Description className="confirm-delete-dialog__description">
                You'll be signed out of your account and returned to the sign-in screen.
              </Dialog.Description>
            </div>
            <div className="confirm-delete-dialog__actions">
              <Dialog.Close asChild>
                <Button type="button" variant="ghost" size="sm" disabled={isSigningOut}>
                  Cancel
                </Button>
              </Dialog.Close>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                disabled={isSigningOut}
                onClick={() => void handleSignOut()}
              >
                {isSigningOut ? 'Logging out…' : 'Log out'}
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}
